"use client"

import { Stack, Text, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import ClientZoom from "@/components/ClientZoom"
import Img from "@/components/Img"
import MarkdownParser from "@/components/MarkdownParser"
import { type FullImageBlock as FullImageBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface FullImageBlockProps extends ContentBlockTitleNumberProps {
  block: FullImageBlockType
  fullImageNumber?: number
}

const FullImageBlock: FC<FullImageBlockProps> = ({
  block,
  fullImageNumber,
  titleNumber,
}) => {
  const directusAssetsUrl = process.env.DIRECTUS_ASSETS_URL || ""
  const {
    darker_bg,
    description,
    image,
    padding,
    show_image_description,
    title,
    two_columns_full_image,
  } = block
  const trimmedTitle = title?.trim()
  const trimmedDescription = description?.trim()
  const imageTitle = image?.title?.trim()
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription =
    trimmedDescription !== undefined &&
    trimmedDescription !== "" &&
    trimmedDescription !== null
  const zoomSrc = image?.id ? `${directusAssetsUrl}${image.id}` : undefined
  const hasImageDescription = Boolean(
    show_image_description && fullImageNumber && imageTitle
  )

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="full-image-block  edge-to-edge"
      direction={{
        base: "column",
        lg: !two_columns_full_image ? "column" : "row",
      }}
      gap={{ base: 8, lg: two_columns_full_image ? 6 : 8 }}
      justifyContent={"flex-end"}
      overflow={two_columns_full_image ? "visible" : "hidden"}
      position="relative"
      px={{
        base: padding ? 8 : 0,
        md: padding ? 10 : 0,
        lg: padding ? 24 : 0,
        xl: padding ? 40 : 0,
      }}
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          display={{ base: two_columns_full_image ? "none" : "flex" }}
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: two_columns_full_image ? "30%" : "100%",
          }}
          pl={{
            base: padding ? 0 : 8,
            md: padding ? 0 : 10,
            lg: padding ? 0 : 24,
            xl: padding ? 0 : 40,
          }}
          pt={padding ? 0 : { base: 12, lg: 24 }}
          {...getStickyContentBlockLeftProps(two_columns_full_image)}
        >
          <BlurFade flexDirection={"column"} gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={description}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-end"
        as="figure"
        data-print-media-block=""
        flexBasis={0}
        flexGrow={1}
        gap={0}
        m={0}
        maxWidth={{
          base: "100%",
          lg: block.two_columns_full_image
            ? block.title !== undefined
              ? "70%"
              : "100%"
            : "100%",
        }}
        pl={!hasTitle && two_columns_full_image ? { base: 0, lg: 12 } : 0}
        w={"full"}
      >
        <BlurFade inView w="full">
          <ClientZoom
            zoomImg={
              zoomSrc
                ? {
                    alt: title ?? "Full width image",
                    src: zoomSrc,
                  }
                : undefined
            }
          >
            <Img
              alt={title ?? "Full width image"}
              aria-label={title ?? "Full width image"}
              breakpointWidths={{
                base: "360",
                sm: "420",
                md: "860",
                lg: "1376",
                xl: "1438",
              }}
              height="auto"
              noContainer
              objectFit="cover"
              rounded={padding ? "3xl" : "none"}
              src={image?.id ?? undefined}
              transform={{
                format: "webp",
                quality: 100,
              }}
              width="100%"
            />
          </ClientZoom>
        </BlurFade>
        {hasImageDescription && (
          <Text
            as="figcaption"
            color="fg.muted"
            fontFamily="mono"
            fontSize="sm"
            lineHeight="short"
            px={6}
            py={4}
            textAlign="left"
            w="full"
          >
            <Text as="span">Fig {fullImageNumber}</Text> - {imageTitle}
          </Text>
        )}
      </VStack>
    </Stack>
  )
}

export default FullImageBlock
